<template>
  <Select
    ref="remoteSelect"
    :value="currentValue"
    :filterable="filterable"
    :clearable="clearable"
    :disabled="disabled"
    :placeholder="placeholder"
    :loading="loading"
    :transfer="transfer"
    :transfer-class-name="dropdownClassName"
    @on-change="handleChange"
    @on-query-change="handleQueryChange"
    @on-open-change="handleOpenChange"
    @on-clear="handleClear"
  >
    <!-- 下拉选项 -->
    <Option
      v-for="item in displayOptions"
      :key="getOptionValue(item)"
      :value="getOptionValue(item)"
      :label="getOptionLabel(item)"
    >
      {{ getOptionLabel(item) }}
    </Option>

    <!-- 底部状态 -->
    <div
      v-if="showLoadMoreStatus"
      class="common-remote-select-status"
    >
      <span v-if="loadingMore">{{ $t('key1000514') }}</span>
      <span v-else-if="finished && innerOptions.length > 0">{{ $t('key1000515') }}</span>
      <span v-else-if="!finished && innerOptions.length > 0">{{ $t('key1000516') }}</span>
    </div>

    <!-- 空数据提示 -->
    <div
      v-if="!loading && displayOptions.length === 0"
      class="common-remote-select-empty"
    >
      {{ $t('key1000104') }}
    </div>
  </Select>
</template>

<script>
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';

/**
 * ============================================================
 * 📦 CommonRemoteSelect 远程下拉组件（Vue2 + iView）
 * ============================================================
 *
 * 🚀 功能：
 * 1. 支持远程搜索（默认 searchValue）
 * 2. 支持滚动加载更多（分页）
 * 3. 支持本地模糊过滤（已加载数据）
 * 4. 支持防抖搜索
 * 5. 支持自定义显示内容
 * 6. 支持额外筛选参数（如 ymsMerchantIds）
 *
 * ============================================================
 * 🧩 一、最简用法（推荐）
 * ============================================================
 *
 * <CommonRemoteSelect
 *   v-model="form.ymsMerchantId"
 *   :request-fn="getMerchantPageList"
 *   :extra-params="merchantSearchParams"
 *   placeholder="请输入商户号搜索"
 * />
 *
 *
 * ============================================================
 * 🧩 二、完整用法（推荐复制）
 * ============================================================
 *
 * <CommonRemoteSelect
 *   ref="merchantSelect"
 *   v-model="form.ymsMerchantId"
 *   :request-fn="getMerchantPageList"
 *   :extra-params="merchantSearchParams"
 *   :props-map="{
 *     value: 'ymsMerchantId',
 *     label: 'ymsMerchantId'
 *   }"
 *   :page-size="20"
 *   :filterable="true"
 *   :remote-search="true"
 *   :enable-local-filter="true"
 *   :auto-load-on-open="true"
 *   :min-query-length="0"
 *   :transfer="false"
 *   placeholder="请输入商户号搜索"
 *   @on-change="handleChange"
 *   @loaded="handleLoaded"
 * />
 *
 *
 * ============================================================
 * 🧩 三、接口说明（当前项目默认适配）
 * ============================================================
 *
 * 📥 入参：
 * {
 *   ...extraParams,
 *   searchValue: '',   // 关键词（组件自动传）
 *   pageNum: 1,        // 页码（组件自动维护）
 *   pageSize: 20       // 每页条数
 * }
 *
 * 📤 出参（支持多种格式）：
 * 优先解析：
 * res.data.datas.list
 * res.data.datas.total
 *
 *
 * ============================================================
 * 🧩 四、Props 参数说明
 * ============================================================
 *
 * 【核心参数】
 *
 * @prop {any} value
 * v-model 绑定值
 *
 * @prop {Function} requestFn  【必传】
 * 请求方法，必须返回 Promise
 *
 * 示例：
 * requestFn(params) {
 *   return this.$axios.post('/api/xxx', params);
 * }
 *
 *
 * @prop {Object} extraParams
 * 接口额外参数（不会被组件覆盖）
 *
 * 示例：
 * {
 *   ymsMerchantIds: [],  // 精确查询多个商户
 *   mobiles: [],
 *   emails: []
 * }
 *
 *
 * ============================================================
 * 🧩 五、字段映射 propsMap
 * ============================================================
 *
 * @prop {Object} propsMap
 *
 * 默认：
 * {
 *   value: 'ymsMerchantId',
 *   label: 'ymsMerchantId',
 *   keyword: 'searchValue',
 *   pageNum: 'pageNum',
 *   pageSize: 'pageSize',
 *   localSearchFields: ['ymsMerchantId', 'name', 'contacts', 'mobile', 'email']
 * }
 *
 * 说明：
 * - keyword：接口关键词字段（默认 searchValue）
 * - localSearchFields：本地过滤字段
 *
 *
 * ============================================================
 * 🧩 六、行为控制参数
 * ============================================================
 *
 * @prop {Boolean} filterable = true
 * 是否允许输入
 *
 * @prop {Boolean} remoteSearch = true
 * 是否启用远程搜索
 *
 * @prop {Boolean} enableLocalFilter = true
 * 是否启用本地过滤
 *
 * @prop {Boolean} autoLoadOnOpen = true
 * 首次打开自动加载
 *
 * @prop {Boolean} autoInit = false
 * 初始化时是否加载
 *
 * @prop {Number} pageSize = 20
 * 每页条数
 *
 * @prop {Number} debounceTime = 300
 * 搜索防抖时间
 *
 * @prop {Number} minQueryLength = 0
 * 最少输入多少字符才触发搜索
 *
 * @prop {Boolean} transfer = true
 * 是否挂载到 body（弹窗中建议 false）
 *
 *
 * ============================================================
 * 🧩 七、事件（Events）
 * ============================================================
 *
 * @event on-change(value, item)
 * 选中变化
 *
 * @event on-query-change(query)
 * 输入变化
 *
 * @event loaded({ list, total, pageNum })
 * 数据加载完成
 *
 * @event load-error(error)
 * 请求失败
 *
 *
 * ============================================================
 * 🧩 八、组件方法（ref 调用）
 * ============================================================
 *
 * this.$refs.xxx.refresh()
 * 👉 重新加载第一页
 *
 * this.$refs.xxx.clearOptions()
 * 👉 清空缓存
 *
 * this.$refs.xxx.setOptions(list)
 * 👉 手动设置数据
 *
 *
 * ============================================================
 * 🧩 九、重要说明（⚠️必看）
 * ============================================================
 *
 * 1️⃣ searchValue 是模糊搜索字段
 * 👉 输入框内容自动传这个
 *
 * 2️⃣ ymsMerchantIds 是精确查询
 * 👉 必须放在 extraParams
 *
 * 3️⃣ 滚动加载不生效常见原因：
 * ❌ 数据不足一页（没有滚动条）
 * ❌ transfer 导致 DOM 不在当前组件
 * ❌ 滚动事件绑定时机太早
 *
 * 4️⃣ Modal 中使用必须：
 * 👉 :transfer="false"
 *
 *
 * ============================================================
 * 🧩 十、推荐写法（最佳实践）
 * ============================================================
 *
 * <CommonRemoteSelect
 *   v-model="form.ymsMerchantId"
 *   :request-fn="getMerchantPageList"
 *   :extra-params="merchantSearchParams"
 *   :transfer="false"
 *   placeholder="请输入商户号搜索"
 * />
 *
 * ============================================================
 */
export default {
  name: 'CommonRemoteSelect',
  props: {
    /**
     * @Description: v-model 绑定值
     */
    value: {
      default: null
    },

    /**
     * @Description: 请求方法（必传）
     * 必须返回 Promise
     *
     * @example
     * requestFn(params) {
     *   return this.$axios.post(url, params);
     * }
     */
    requestFn: {
      type: Function,
      required: true
    },

    /**
     * @Description: 接口额外参数
     * 这些参数会和 searchValue、pageNum、pageSize 一起合并后传给接口
     *
     * 例如：
     * {
     *   registerStartTime: null,
     *   registerEndTime: null,
     *   registerSource: null,
     *   distributorAuditStatus: null,
     *   ymsMerchantIds: [], // 多个商户号精确查询
     *   mobiles: [],
     *   emails: [],
     *   labelNames: [],
     *   distributorMerchantGradeId: null,
     *   distributorMerchantGroupId: null
     * }
     */
    extraParams: {
      type: Object,
      default: () => ({})
    },

    /**
     * @Description: 字段映射配置
     *
     * value：Option 对应的值字段
     * label：Option 显示字段
     * keyword：接口关键词参数字段，默认 searchValue
     * pageNum：接口页码字段
     * pageSize：接口分页条数字段
     * localSearchFields：本地过滤时匹配的字段数组
     *
     * 默认值：
     * {
     *   value: 'ymsMerchantId',
     *   label: 'ymsMerchantId',
     *   keyword: 'searchValue',
     *   pageNum: 'pageNum',
     *   pageSize: 'pageSize',
     *   localSearchFields: ['ymsMerchantId', 'name', 'contacts', 'mobile', 'email']
     * }
     */
    propsMap: {
      type: Object,
      default: () => ({})
    },

    /**
     * @Description: 每页条数
     */
    pageSize: {
      type: Number,
      default: 20
    },

    /**
     * @Description: 占位文本
     */
    placeholder: {
      type: String,
      default: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000101')
    },

    /**
     * @Description: 是否支持清空
     */
    clearable: {
      type: Boolean,
      default: true
    },

    /**
     * @Description: 是否禁用
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * @Description: 是否支持输入过滤
     */
    filterable: {
      type: Boolean,
      default: true
    },

    /**
     * @Description: 是否开启远程搜索
     * true：输入关键词后调接口
     * false：只做本地过滤
     */
    remoteSearch: {
      type: Boolean,
      default: true
    },

    /**
     * @Description: 是否开启本地过滤
     * true：对当前已加载的数据继续做前端模糊过滤
     */
    enableLocalFilter: {
      type: Boolean,
      default: true
    },

    /**
     * @Description: 首次展开下拉是否自动加载
     */
    autoLoadOnOpen: {
      type: Boolean,
      default: true
    },

    /**
     * @Description: 组件创建时是否自动加载
     */
    autoInit: {
      type: Boolean,
      default: false
    },

    /**
     * @Description: 是否将下拉挂载到 body
     */
    transfer: {
      type: Boolean,
      default: true
    },

    /**
     * @Description: 搜索防抖时间（毫秒）
     */
    debounceTime: {
      type: Number,
      default: 300
    },

    /**
     * @Description: 最少输入多少字符后才触发远程搜索
     * 0 表示不限制
     */
    minQueryLength: {
      type: Number,
      default: 0
    },

    /**
     * @Description: 自定义 option 显示文本
     * 不传时默认使用 label 字段
     *
     * @example
     * optionLabelFormatter(item) {
     *   return item.ymsMerchantId + ' - ' + (item.contacts || '');
     * }
     */
    optionLabelFormatter: {
      type: Function,
      default: null
    },

    /**
     * @Description: 是否在关闭下拉时清空输入关键词
     * 默认 false，保留关键词
     */
    clearQueryOnClose: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 当前选中的值
      currentValue: this.value,

      // 当前输入的关键词
      query: '',

      // 当前已加载的下拉数据
      innerOptions: [],

      // 当前页码
      innerPageNum: 1,

      // 总条数
      total: 0,

      // 首屏/重置加载状态
      loading: false,

      // 加载更多状态
      loadingMore: false,

      // 是否全部加载完成
      finished: false,

      // 下拉是否展开
      dropdownVisible: false,

      // 是否已经初始化加载过
      hasInitLoaded: false,

      // 滚动容器
      scrollWrap: null,

      // 防抖 timer
      debounceTimer: null,

      // 给下拉层生成唯一类名，方便查找真实 DOM
      uniqueId: 'common-remote-select-' + Date.now() + '-' + Math.floor(Math.random() * 10000)
    };
  },

  computed: {
    /**
     * @Description: 合并后的字段映射
     */
    fieldMap() {
      return {
        value: this.propsMap.value || 'ymsMerchantId',
        label: this.propsMap.label || 'ymsMerchantId',
        keyword: this.propsMap.keyword || 'searchValue',
        pageNum: this.propsMap.pageNum || 'pageNum',
        pageSize: this.propsMap.pageSize || 'pageSize',
        localSearchFields: this.propsMap.localSearchFields || ['ymsMerchantId', 'name', 'contacts', 'mobile', 'email']
      };
    },

    /**
     * @Description: 下拉层唯一 class 名
     */
    dropdownClassName() {
      return this.uniqueId;
    },

    /**
     * @Description: 下拉中实际显示的数据
     * 仅对当前已加载的数据进行本地过滤
     */
    displayOptions() {
      let list = this.innerOptions || [];

      if (this.enableLocalFilter && this.query) {
        const keyword = String(this.query).toLowerCase();

        list = list.filter(item => {
          return this.fieldMap.localSearchFields.some(field => {
            const fieldValue = item[field] == null ? '' : String(item[field]).toLowerCase();
            return fieldValue.includes(keyword);
          });
        });
      }

      return list;
    },

    /**
     * @Description: 是否显示底部状态
     */
    showLoadMoreStatus() {
      return this.dropdownVisible && this.innerOptions.length > 0;
    }
  },

  watch: {
    /**
     * @Description: 外部 value 改变时同步内部值
     */
    value(val) {
      this.currentValue = val;
    },

    /**
     * @Description: 额外参数变化时重新加载
     */
    extraParams: {
      deep: true,
      handler() {
        this.query = '';
        this.refresh();
      }
    }
  },

  created() {
    if (this.autoInit) {
      this.getList({reset: true});
    }
  },

  beforeDestroy() {
    this.removeScrollListener();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  },

  methods: {
    /**
     * @Description: 获取当前 option 的 value
     * @param {Object} item 当前选项对象
     * @returns {String|Number}
     */
    getOptionValue(item) {
      return item[this.fieldMap.value];
    },

    /**
     * @Description: 获取当前 option 的显示文本
     * @param {Object} item 当前选项对象
     * @returns {String}
     */
    getOptionLabel(item) {
      if (typeof this.optionLabelFormatter === 'function') {
        return this.optionLabelFormatter(item);
      }
      return item[this.fieldMap.label] == null ? '' : String(item[this.fieldMap.label]);
    },

    /**
     * @Description: 选中值变化
     * @param {String|Number} val 当前选中的值
     */
    handleChange(val) {
      this.currentValue = val;
      this.$emit('input', val);
      this.$emit('on-change', val, this.getSelectedItem(val));
    },

    /**
     * @Description: 清空选择
     */
    handleClear() {
      this.query = '';
      this.$emit('on-clear');
      this.refresh();
    },

    /**
     * @Description: 输入关键词变化
     * @param {String} query 输入的关键词
     */
    handleQueryChange(query) {
      this.query = query || '';
      this.$emit('on-query-change', this.query);
      // 空值不请求
      if (!this.query) {
        this.clearOptions(false);

        if (this.autoLoadOnOpen) {
          this.getList({ reset: true });
        }
        return;
      }
      if (!this.remoteSearch) return;
      // 最小长度控制
      if (this.query.length < this.minQueryLength) return;
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      const currentQuery = this.query;
      this.debounceTimer = setTimeout(() => {
        // 防止请求返回顺序错乱
        if (this.query !== currentQuery) return;
        this.getList({reset: true});
      }, this.debounceTime);
    },

    /**
     * @Description: 下拉展开/关闭
     * 这里不要只用一次 nextTick，iView Select 下拉带过渡动画，DOM 可能还没真正挂好
     * @param {Boolean} open 是否展开
     */
    handleOpenChange(open) {
      this.dropdownVisible = open;

      if (open) {
        // 首次展开自动加载
        if (this.autoLoadOnOpen && !this.hasInitLoaded) {
          this.getList({reset: true});
        }

        // 延迟 + 重试绑定滚动事件，避免下拉 DOM 还没渲染完成
        this.bindScrollListenerWithRetry();
      } else {
        this.removeScrollListener();

        if (this.clearQueryOnClose) {
          this.query = '';
        }
      }

      this.$emit('on-open-change', open);
    },

    /**
     * @Description: 获取下拉数据
     * @param {Object} options 配置项
     * @param {Boolean} options.reset 是否重置列表
     */
    async getList({reset = false} = {}) {
      if (this.loading || this.loadingMore) return;

      if (reset) {
        this.innerPageNum = 1;
        this.total = 0;
        this.finished = false;
      }

      if (this.finished && !reset) return;

      const isFirstPage = this.innerPageNum === 1;

      if (isFirstPage) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      try {
        const params = {
          ...this.extraParams,
          [this.fieldMap.keyword]: this.query,
          [this.fieldMap.pageNum]: this.innerPageNum,
          [this.fieldMap.pageSize]: this.pageSize
        };

        const res = await this.requestFn(params);
        const {list, total} = this.normalizeResponse(res);

        if (reset) {
          this.innerOptions = list;
        } else {
          this.innerOptions = this.mergeOptions(this.innerOptions, list);
        }

        this.total = total;

        // 判断是否已经加载完
        if (list.length < this.pageSize) {
          this.finished = true;
        }

        if (typeof total === 'number' && this.innerOptions.length >= total) {
          this.finished = true;
        }

        if (!list.length && this.innerPageNum === 1) {
          this.finished = true;
        }

        this.hasInitLoaded = true;

        this.$emit('loaded', {
          list: this.innerOptions,
          total: this.total,
          pageNum: this.innerPageNum,
          query: this.query
        });

        if (!this.finished) {
          this.innerPageNum += 1;
        }

        // 数据变更后重新绑定一次，防止下拉节点重绘
        if (this.dropdownVisible) {
          this.bindScrollListenerWithRetry();
        }
      } catch (error) {
        this.$emit('load-error', error);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    /**
     * @Description: 加载下一页
     */
    async loadMore() {
      if (this.loading || this.loadingMore || this.finished) return;
      await this.getList({reset: false});
    },

    /**
     * @Description: 带重试的滚动绑定
     * iView Select 的下拉有过渡动画，真实 DOM 可能不是立刻可拿到
     */
    bindScrollListenerWithRetry() {
      let count = 0;
      const maxCount = 8;

      const tryBind = () => {
        count += 1;
        const success = this.bindScrollListener();

        if (!success && count < maxCount && this.dropdownVisible) {
          setTimeout(tryBind, 80);
        }
      };

      this.$nextTick(() => {
        setTimeout(tryBind, 0);
      });
    },

    /**
     * @Description: 获取真实的下拉滚动容器
     * 不同版本/场景下，真正滚动的节点不一定完全一样，这里做兼容兜底
     * @returns {Element|null}
     */
    getDropdownScrollWrap() {
      const dropdown = document.querySelector(`.${this.dropdownClassName}`);
      if (!dropdown) return null;

      // 优先找常见滚动节点
      let wrap =
        dropdown.querySelector('.ivu-select-dropdown-list') ||
        dropdown.querySelector('.ivu-select-dropdown') ||
        dropdown;

      // 如果当前节点不可滚动，则向上/向内再找一层真正有滚动条的容器
      const candidates = [
        wrap,
        wrap && wrap.parentNode,
        dropdown.querySelector('.ivu-select-dropdown'),
        dropdown
      ].filter(Boolean);

      for (let i = 0; i < candidates.length; i++) {
        const el = candidates[i];
        if (el && el.scrollHeight > el.clientHeight) {
          return el;
        }
      }

      // 没有找到真正产生滚动条的节点时，退回默认节点
      return wrap || null;
    },

    /**
     * @Description: 绑定滚动事件
     * @returns {Boolean} 是否绑定成功
     */
    bindScrollListener() {
      this.removeScrollListener();

      const wrap = this.getDropdownScrollWrap();
      if (!wrap) return false;

      this.scrollWrap = wrap;
      this.scrollWrap.addEventListener('scroll', this.handleScroll, {passive: true});

      return true;
    },

    /**
     * @Description: 移除滚动事件
     */
    removeScrollListener() {
      if (this.scrollWrap) {
        this.scrollWrap.removeEventListener('scroll', this.handleScroll, {passive: true});
        this.scrollWrap = null;
      }
    },

    /**
     * @Description: 监听滚动到底部，自动加载更多
     * @param {Event} e 事件对象
     */
    handleScroll(e) {
      const target = e.target || this.scrollWrap;
      if (!target) return;

      const distance = target.scrollHeight - target.scrollTop - target.clientHeight;

      // 离底部 20px 以内触发
      if (distance <= 20) {
        this.loadMore();
      }
    },

    /**
     * @Description: 标准化接口返回
     * @param {*} res 接口返回结果
     * @returns {{list: Array, total: Number}}
     */
    normalizeResponse(res) {
      let list = [];
      let total = 0;

      if (res && res.data && res.data.datas && Array.isArray(res.data.datas.list)) {
        list = res.data.datas.list;
        total = Number(res.data.datas.total) || 0;
      } else if (res && res.datas && Array.isArray(res.datas.list)) {
        list = res.datas.list;
        total = Number(res.datas.total) || 0;
      } else if (res && Array.isArray(res.list)) {
        list = res.list;
        total = typeof res.total === 'number' ? res.total : list.length;
      } else if (res && res.data && Array.isArray(res.data.list)) {
        list = res.data.list;
        total = typeof res.data.total === 'number' ? res.data.total : list.length;
      } else if (Array.isArray(res)) {
        list = res;
        total = res.length;
      }

      return {
        list,
        total
      };
    },

    /**
     * @Description: 合并新旧选项并去重
     * @param {Array} oldList 旧数据
     * @param {Array} newList 新数据
     * @returns {Array}
     */
    mergeOptions(oldList = [], newList = []) {
      const map = {};
      const result = [];

      [...oldList, ...newList].forEach(item => {
        const key = item[this.fieldMap.value];
        if (!map[key]) {
          map[key] = true;
          result.push(item);
        }
      });

      return result;
    },

    /**
     * @Description: 根据 value 获取当前选中的完整对象
     * @param {String|Number} val 当前选中的值
     * @returns {Object|null}
     */
    getSelectedItem(val) {
      return this.innerOptions.find(item => item[this.fieldMap.value] === val) || null;
    },

    /**
     * @Description: 刷新第一页
     */
    refresh() {
      this.getList({reset: true});
    },

    /**
     * @Description: 清空缓存的选项数据
     * @param {Boolean} clearQuery 是否同时清空搜索关键词
     */
    clearOptions(clearQuery = true) {
      this.innerOptions = [];
      this.innerPageNum = 1;
      this.total = 0;
      this.finished = false;
      this.hasInitLoaded = false;

      if (clearQuery) {
        this.query = '';
      }
    },

    /**
     * @Description: 手动设置选项数据（不走接口）
     * @param {Array} list 选项数组
     */
    setOptions(list = []) {
      this.innerOptions = Array.isArray(list) ? list : [];
      this.total = this.innerOptions.length;
      this.finished = true;
      this.hasInitLoaded = true;
    }
  }
};
</script>

<style scoped>
.common-remote-select-status,
.common-remote-select-empty {
  padding: 8px 12px;
  text-align: center;
  color: #999;
  font-size: 12px;
}
</style>